import classNames from "classnames";
import { Checkbox } from "components/Checkbox/Checkbox";
import {
    AttributeKeyValue,
    AttributeOptionChoice,
    FilteredDropdown,
} from "components/FilteredDropdown";
import { FormLabel } from "components/Input/FormLabel";
import { LoadingOverlay } from "components/Loading/LoadingOverlay";
import { RadioButton } from "components/RadioButton/RadioButton";
import { usePartiallyConfiguredProduct } from "components/productContext";
import { useHideDebugContent } from "helpers/debug";
import {
    AttributeOptionDetails,
    AttributeOptionPlusInfo,
    Configurator,
} from "interfaces/products";
import React from "react";

import styles from "./product-code-configurator.module.scss";

export type FormState = (readonly [string, string])[];

interface ProductCodeConfiguratorProps {
    details: AttributeOptionDetails;
    loading: boolean;
    onChange?: (data: AttributeKeyValue) => void;
    actionButtons: React.ReactNode;
    /**
     * Original designs intend this for use in "Edit Product Options" feature of Favorites alone
     */
    title?: string;
    /**
     * Modal has the one-column margins built-in, so columns need tweaked depending on placement.
     * "inline" uses default columns so it is treated like the default.
     */
    placement?: "inline" | "modal";
    selectedOptions: AttributeOptionPlusInfo[];
}

export function getConfiguratorName(configurator: Configurator) {
    return configurator.display_name?.replace(/\s/g, "-"); // TODO: this should never be null
}

/**
 * In a situation where you get two codes like C8100ASUS19 and 8100ASUS19, this will tell you
 * if they're the same except for one having a C in front, which means concealed fastener.
 */
const hasLeadingModifier = (codes: string[], term: string) => {
    if (codes.length !== 2) return false;
    // Shorter one first
    const sorted = codes.sort((a, b) => a.length - b.length);
    const split = sorted[1].split(sorted[0]);
    if (split[0].toUpperCase() === term && split[1] === "") return true;
    return false;
};

export function ProductCodeConfigurator({
    onChange,
    details,
    loading,
    title,
    actionButtons,
    selectedOptions,
    placement = "inline",
}: ProductCodeConfiguratorProps) {
    const { pageTitle, productCodes } = usePartiallyConfiguredProduct();

    /** DEPRECATED but still here until the radio/selects get reconciled to the combobox work (TODO) */
    const onInputFieldChange = () => {
        if (!onChange) return;
        // const formData = check(encodableForm.decode([...new FormData(formRef.current as any)]));
        // setFormState(formData);
        // onChange(formData);
    };

    const onComboBoxChange = (attributeKeyValue: AttributeKeyValue) => {
        if (!onChange) return;
        onChange(attributeKeyValue);
    };

    const hideDebugContent = useHideDebugContent();
    const numCodes = productCodes.length;
    const fullySelected =
        selectedOptions.length === details.configurator.length;

    return (
        <section className={classNames([styles.root, styles[placement]])}>
            {typeof title === "string" && (
                <header>
                    <h1
                        id="configurator-modal"
                        className={classNames(["h4", styles.heading])}
                    >
                        {title}
                    </h1>
                </header>
            )}
            <div className={styles.content}>
                {loading && (
                    <LoadingOverlay visuallyHiddenText="Configuration is loading…" />
                )}
                <form className={styles.form}>
                    <div className={styles.disabledInput}>
                        <FormLabel htmlFor="groupInput">Product</FormLabel>
                        <input
                            id="groupInput"
                            type="text"
                            value={pageTitle?.pageTitle || details.option}
                            disabled
                        />
                        <input
                            type="hidden"
                            name={details.product_attribute_id.toString()}
                            value={details.id}
                        />
                    </div>
                    {details.configurator.map((configurator) => {
                        const values = configurator.options;
                        const key = getConfiguratorName(configurator);
                        const label = configurator.display_name;

                        const getDefaultValue: () => AttributeOptionChoice | null =
                            () => {
                                const match = selectedOptions.find((option) =>
                                    values.find(
                                        (value) => value.id === option.id,
                                    ),
                                );
                                if (!match) return null;
                                const choice: AttributeOptionChoice = {
                                    value: [
                                        match.attribute.id,
                                        match.id,
                                    ] as const,
                                    label: `${match.option}${
                                        match.code ? ` (${match.code})` : ""
                                    }`,
                                };
                                return choice;
                            };

                        // TODO get this from somewhere configurable and update radio and checkbox to work the
                        // same way that select does
                        switch ("select" as "select" | "radio" | "checkbox") {
                            case "select":
                                return (
                                    <div key={key}>
                                        <FormLabel>{label}</FormLabel>
                                        <FilteredDropdown
                                            choices={values.map((value) => ({
                                                value: [
                                                    value.attribute.id,
                                                    value.id,
                                                ] as const,
                                                label: `${value.option}${
                                                    value.code
                                                        ? ` (${value.code})`
                                                        : ""
                                                }`,
                                            }))}
                                            aria-label={label}
                                            placeholder={`Select ${label}`}
                                            onChange={onComboBoxChange}
                                            defaultValue={getDefaultValue()}
                                        />
                                    </div>
                                );
                            case "radio":
                                return (
                                    <fieldset
                                        className={styles.fieldset}
                                        key={key}
                                    >
                                        <legend>{label}</legend>
                                        <span>
                                            {values.map((value) => (
                                                <RadioButton
                                                    key={value.id}
                                                    value={value.id}
                                                    name={key}
                                                    onChange={
                                                        onInputFieldChange
                                                    }
                                                >
                                                    {value.option}
                                                    {value.code !== null &&
                                                        ` (${value.code})`}
                                                </RadioButton>
                                            ))}
                                        </span>
                                    </fieldset>
                                );
                            case "checkbox":
                                return (
                                    <fieldset
                                        className={styles.fieldset}
                                        key={key}
                                    >
                                        <legend>{label}</legend>
                                        <span>
                                            {values.map((value) => (
                                                <Checkbox
                                                    key={value.id}
                                                    value={String(value.id)}
                                                    defaultChecked={false}
                                                    onChange={
                                                        onInputFieldChange
                                                    }
                                                >
                                                    {value.option}
                                                    {value.code !== null &&
                                                        ` (${value.code})`}
                                                </Checkbox>
                                            ))}
                                        </span>
                                    </fieldset>
                                );
                        }
                    })}
                </form>
                <aside className={styles.details}>
                    <div>
                        <div className={styles.detailTitle}>Product Name:</div>
                        <p className={styles.detailName}>
                            {pageTitle?.configString || details.option}
                        </p>
                    </div>
                    <div>
                        <div className={styles.detailTitle}>Product Code:</div>
                        {numCodes === 0 && (
                            <p className={styles.noProductCode}>
                                Contact a dealer to see if this product comes in
                                the selected configuration
                            </p>
                        )}
                        {numCodes === 1 && (
                            <p className={styles.productCode}>
                                {productCodes[0]}
                            </p>
                        )}
                        {(numCodes === 2 ||
                            numCodes === 3 ||
                            numCodes === 4 ||
                            numCodes === 5) && (
                            <>
                                <ul className={styles.productCode}>
                                    {productCodes.map((code) => {
                                        return <li key={code}>{code}</li>;
                                    })}
                                </ul>
                                {/(RH|LH)/.test(productCodes[0]) &&
                                    /(RH|LH)/.test(productCodes[1]) && (
                                        <p
                                            className={classNames([
                                                styles.noProductCode,
                                                styles.handing,
                                            ])}
                                        >
                                            There are multiple codes due to
                                            product configuration. Please reach
                                            out to a dealer for more
                                            information.
                                        </p>
                                    )}
                                {hasLeadingModifier(productCodes, "C") && (
                                    <p
                                        className={classNames([
                                            styles.noProductCode,
                                            styles.handing,
                                        ])}
                                    >
                                        There are two codes because this
                                        configuration is also available with a
                                        concealed fastener.
                                    </p>
                                )}
                                {hasLeadingModifier(productCodes, "EMP") && (
                                    <p
                                        className={classNames([
                                            styles.noProductCode,
                                            styles.handing,
                                        ])}
                                    >
                                        There are two codes because this
                                        configuration is also available with an
                                        electronic lock.
                                    </p>
                                )}
                            </>
                        )}
                        {numCodes > 5 && fullySelected && (
                            <p className={styles.noProductCode}>
                                Contact a dealer to see if this product comes in
                                the selected configuration
                            </p>
                        )}
                        {numCodes > 5 && !fullySelected && (
                            <p className={styles.noProductCode}>
                                Select more options to get a product code.
                            </p>
                        )}
                    </div>
                    <div>{actionButtons}</div>
                    {numCodes > 0 && (
                        <pre hidden={hideDebugContent}>
                            Number of results: {numCodes}
                            <br />
                            <ul>
                                {productCodes.map((code) => (
                                    <li key={code}>{code}</li>
                                ))}
                            </ul>
                        </pre>
                    )}
                </aside>
            </div>
        </section>
    );
}
